export function isObject(obj: any): obj is object {
  return obj && typeof obj === 'object';
}

export function hasObjectData(obj: any): obj is object {
  return isObject(obj) && Object.keys(obj).length > 0;
}

export function objectRealValues(obj: any): any[] {
  return Object.values(obj || {}).filter((value) => !isNullOrUndefined(value));
}

export function isNullOrUndefined(value: unknown): boolean {
  return value == null;
}

export function filterObject<T>(
  obj: T,
  filterFunc: (value: unknown, key?: string) => boolean = (value) => !isNullOrUndefined(value),
): Partial<T> | undefined {
  if (isNullOrUndefined(obj)) {
    return;
  }

  return Object.entries(obj).reduce(
    (filtered, [key, value]) => ({
      ...filtered,
      ...(filterFunc(value, key) ? { [key]: value } : {}),
    }),
    {} as Partial<T>,
  );
}

export function fromEntries(iterable: Iterable<any>): object {
  return Array.from(iterable).reduce((o, i) => ({ ...o, [i[0]]: i[1] }), {});
}

// use with caution
export function isDeepEqual<T>(subj1: T, subj2: T, ignoreEmpty?: boolean): boolean {
  const processed: any[] = [];

  function isEqual(a: any, b: any): boolean {
    if (a === b || (ignoreEmpty && (a == null || a === '') && (b == null || b === ''))) {
      return true;
    }

    if (!a || !b) {
      return false;
    }

    if (typeof a !== 'object' || typeof b !== 'object') {
      return false;
    }

    if (processed.includes(a) || processed.includes(b)) {
      // don't want to handle recursive objects
      return false;
    }

    processed.push(a, b);

    const aKeys = Object.keys(a).filter((key) => !ignoreEmpty || (a[key] != null && a[key] !== ''));
    const bKeys = Object.keys(b).filter((key) => !ignoreEmpty || (b[key] != null && b[key] !== ''));

    if (aKeys.length !== bKeys.length) {
      return false;
    }

    return aKeys.every((key) => b.hasOwnProperty(key) && isEqual(a[key], b[key]));
  }

  return isEqual(subj1, subj2);
}

export function assertObject<T extends object>(obj: T): T {
  return obj != null ? obj : ({} as T);
}

export function getValueByPath<T = unknown>(object: Record<string, unknown>, path: string, separator = '.'): T {
  return path.split(separator).reduce((a, b) => a?.[b], object) as T;
}

export function KeysChanges(keys: string[], a: Record<string, string>, b: Record<string, string>): boolean {
  return keys.filter((key) => a[key] !== b[key]).length > 0;
}

export function groupBy<T>(items: T[], key: string, asArray: false): Record<string, T[]>;

export function groupBy<T>(
  items: T[],
  key: string,
  asArray: true,
): {
  key: string;
  data: T[];
}[];

export function groupBy<T>(items: T[], key: string, asArray = false) {
  const obj: Record<string, T[]> = items.reduce(function (acc, curr) {
    (acc[curr[key]] = acc[curr[key]] || []).push(curr);
    return acc;
  }, {});

  if (asArray) {
    return Object.keys(obj).map((key) => ({
      key,
      data: obj[key],
    }));
  } else return obj;
}

export function deepClone<T extends object>(obj: T): T {
  return typeof structuredClone === 'function' ? structuredClone(obj) : JSON.parse(JSON.stringify(obj));
}

export function replaceStringInKey<T extends object>(obj: T, search: string, target: string): T {
  return Object.entries(obj).reduce(
    (parsed, [key, value]) => ({
      ...parsed,
      [key.replace(search, target)]: value,
    }),
    {} as T,
  );
}
export function NullifyUndefinedValues<T extends object>(arr: T[]): T[] {
  return arr.map((item: T) => {
    const dt: T = { ...item };
    Object.keys(item).forEach((key) => {
      if (dt[key] === undefined) dt[key] = null;
    });
    return dt;
  });
}
export function joinObject(object: any, glue: string, separator: string) {
  if (glue === undefined) {
    glue = '=';
  }

  if (separator === undefined) {
    separator = ',';
  }

  return Object.getOwnPropertyNames(object)
    .map((k) => {
      return [k, object[k]].join(glue);
    })
    .join(separator);
}
